import React, { useContext, useEffect, useMemo, useState } from "react";
import PerfectScrollbar from "react-perfect-scrollbar";
import { useIntersection } from "react-use";

import Fab from "@mui/material/Fab";
import AddIcon from "@mui/icons-material/Add";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Fade from "@mui/material/Fade";
import Paper from "@mui/material/Paper";
import { styled } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";

import { CaseContext } from "contexts/CaseContext";

import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { NewPostDialog } from "components/CaseManagement/CaseStream/NewPostDialog";
import NewReportDialog from "components/CaseManagement/CaseStream/NewReportDialog";
import TextStreamItem from "components/CaseManagement/CaseStream/StreamCards/TextStreamItem";
import { NewWorkOrderDialog } from "components/CaseManagement/CaseStream/NewWorkOrderDialog";
import { CreateCaseActionDialog } from "../CaseForms/CreateCaseActionDialog";
import { useGetMachineById } from "hooks/machines/useGetMachineById";
import { CaseActionsContext } from "contexts/CaseActionsContext";
import { SimilarCasesCard } from "components/CaseManagement/SimilarCases/SimilarCasesCard";

import buildUserName from "core/utils/build_user_name";
import { useCaseStatusEnums } from "hooks/useCaseStatusEnums";
import { FeaturesContext } from "contexts/FeaturesContext";

import { CaseStreamItem, CaseStreamItemType } from "interfaces/case-stream";
import { FailureType } from "interfaces/failure-type";
import { EventCard } from "components/CaseManagement/CaseStream/EventCards/EventCard";
import { CaseActionCard } from "components/CaseManagement/CaseStream/CaseActionCard";
import { WorkOrderCard } from "components/CaseManagement/CaseStream/WorkOrderCard";
import { useGet } from "hooks/axios";
import { useSitesQuery } from "hooks/queries/useSitesQuery";
import { useStreamFeedQuery } from "hooks/queries/useStreamFeedQuery";
import {
  CaseStreamFilterType,
  useCaseStreamFilterTypesQuery
} from "hooks/queries/cases";
import { Autocomplete } from "components/Autocomplete";

interface CaseStreamFeedProps {
  caseId: number;
  casePageSaving: boolean;
}

interface FailureTypesResponse {
  results: FailureType[];
  count: number;
}

interface FailureTypeParams {
  size: number;
  detail: boolean;
}

interface IGetFailureTypes {
  loading: boolean;
  error: boolean;
  failureTypes: FailureType[];
}

const useGetFailureTypes = (): IGetFailureTypes => {
  const params = useMemo<FailureTypeParams>(
    () => ({ size: 99999999, detail: true }),
    []
  );
  const { loading, error, data = { results: [], count: 0 } } = useGet<
    FailureTypesResponse,
    FailureTypeParams
  >("failure_types", params);

  return { loading, error, failureTypes: data.results };
};

export function CaseStreamFeed({
  caseId,
  casePageSaving
}: CaseStreamFeedProps): JSX.Element {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [newPostDialogOpen, setNewPostDialogOpen] = useState(false);
  const [newActionDialogOpen, setNewActionDialogOpen] = useState(false);
  const [newReportDialogOpen, setNewReportDialogOpen] = useState(false);
  const [newWorkOrderDialogOpen, setNewWorkOrderDialogOpen] = useState(false);
  const [newReportVariant, setNewReportVariant] = useState<string>("");
  const [selectedFilterType, setSelectedFilterType] = useState<
    CaseStreamFilterType[] | null
  >(null);

  const intersectionRef = React.useRef(null);
  const intersection = useIntersection(intersectionRef, {
    root: null,
    rootMargin: "0px",
    threshold: 1
  });

  const { hasFeature } = useContext(FeaturesContext);
  const { refetchActions } = useContext(CaseActionsContext);
  const {
    caseData,
    loading: isCaseContextLoading,
    error: isCaseContextError
  } = useContext(CaseContext);
  const { data: _sites } = useSitesQuery();
  const { machine } = useGetMachineById(caseData?.machine);

  const queryClient = useQueryClient();

  const workOrderAccess = hasFeature("work_orders");

  const filterTypeValues = useMemo(() => {
    let filterValues = selectedFilterType?.map(item => item.value ?? null);

    // add "info" to filters so that the text dividers are in the feed
    if (filterValues?.length) {
      filterValues = ["info", ...filterValues];
    }

    return filterValues;
  }, [selectedFilterType]);

  const {
    data,
    status,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    isInitialLoading
  } = useStreamFeedQuery(caseId, filterTypeValues);

  const {
    data: filterTypes,
    isLoading: isFilterTypesLoading,
    isFetching: isFilterTypesFetching,
    isError: isFilterTypesError
  } = useCaseStreamFilterTypesQuery();

  const isStreamFeedQueryLoading =
    status === "loading" || isFetching || isFetchingNextPage;
  const isLoading = isStreamFeedQueryLoading || isCaseContextLoading;

  const isStreamFeedQueryError = status === "error";
  const isError = isStreamFeedQueryError || isCaseContextError;

  const streamFeedItems = useMemo(
    () => data?.pages.flatMap(page => page.results) ?? [],
    [data]
  );

  const handleLoadMoreEvents = (): void => {
    if (hasNextPage) {
      fetchNextPage();
    }
  };

  useEffect(() => {
    if (
      intersection?.isIntersecting &&
      hasNextPage &&
      !isFetching &&
      !isInitialLoading
    ) {
      handleLoadMoreEvents();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    intersection?.isIntersecting,
    hasNextPage,
    isFetchingNextPage,
    isInitialLoading
  ]);

  // ensure variable is defined for typescript
  const sites = useMemo(() => _sites?.results ?? [], [_sites]);

  const site = useMemo(() => sites.find(({ id }) => id === machine?.site), [
    sites,
    machine?.site
  ]);

  const { data: caseStatusData } = useCaseStatusEnums(
    caseData?.scalar_configurations
  );

  const { failureTypes } = useGetFailureTypes();

  const component = caseStatusData?.componentValues?.find(
    ({ id }: { id: number }) => id === caseData?.component
  );

  const failureMode = failureTypes.find(
    ({ id }) => id === caseData?.failure_mode
  );

  const openAddMenu = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    setAnchorEl(event.currentTarget);
  };

  const closeAddMenu = (): void => {
    setAnchorEl(null);
  };

  const openNewPostDialog = (): void => {
    closeAddMenu();
    setNewPostDialogOpen(true);
  };

  const openNewCaseActionDialog = (): void => {
    closeAddMenu();
    setNewActionDialogOpen(true);
  };

  const openNewWorkOrderDialog = (): void => {
    closeAddMenu();
    setNewWorkOrderDialogOpen(true);
  };

  const openNewReportDialog = (reportVariant: string): void => {
    closeAddMenu();
    setNewReportVariant(reportVariant);
    setNewReportDialogOpen(true);
  };

  const handleSelectFilterType = (
    selectedFilterType: CaseStreamFilterType[]
  ): void => {
    setSelectedFilterType(selectedFilterType);
  };

  return (
    <>
      <ScrollBar>
        <SimilarCasesCard />
        <h4>Timeline</h4>

        <StyledPaper>
          <Autocomplete
            multiple
            label="Filter Timeline"
            placeholder="Filter Timeline"
            disabled={isFilterTypesLoading || isFilterTypesError}
            loading={isFilterTypesLoading || isFilterTypesFetching}
            error={isFilterTypesError}
            size="small"
            autoHighlight
            disableCloseOnSelect
            blurOnSelect
            clearOnBlur
            disableClearable
            autoComplete
            options={filterTypes?.results ?? []}
            noOptionsText="No options found"
            getOptionLabel={(option: CaseStreamFilterType) => option.name}
            isOptionEqualToValue={(
              option: CaseStreamFilterType,
              value: CaseStreamFilterType
            ) => option.name === value.name}
            onChange={(
              _event: React.ChangeEvent,
              selectedFilterType: CaseStreamFilterType[]
            ) => handleSelectFilterType(selectedFilterType)}
            value={selectedFilterType ?? []}
          />
        </StyledPaper>

        <FeedWrapper>
          {streamFeedItems?.map(item => (
            <StreamItem
              key={item.item_id}
              item={item}
              caseId={caseId}
              refetchStream={() =>
                queryClient.invalidateQueries(["streamFeed", caseId])
              }
            />
          ))}
          <div
            id="intersection-ref"
            ref={intersectionRef}
            style={{ height: "1px" }}
          ></div>
          {!caseData && !isLoading ? (
            <TextStreamItem text="Nothing to show" />
          ) : null}
          {!isLoading && (
            <TextStreamItem
              text={`${buildUserName(caseData?.reporter)} created case on`}
              date={caseData?.create_date}
            />
          )}
          {isLoading && (
            <ProgressIndicatorContainer loading={isLoading} error={isError} />
          )}{" "}
        </FeedWrapper>
      </ScrollBar>
      <Fade
        in={!isLoading && !casePageSaving}
        {...(!isLoading && !casePageSaving ? { timeout: 1000 } : {})}
      >
        <AddFeedItemButton
          id="add-feed-button"
          data-qa="CP-Button-Add-Menu"
          color="primary"
          aria-label="add"
          onClick={openAddMenu}
          disabled={casePageSaving || isLoading}
        >
          <AddIcon />
        </AddFeedItemButton>
      </Fade>
      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        anchorOrigin={{ horizontal: "center", vertical: "top" }}
        transformOrigin={{ horizontal: "center", vertical: "bottom" }}
        onClose={closeAddMenu}
      >
        <MenuItem onClick={openNewPostDialog}>Post</MenuItem>
        <MenuItem
          data-qa="CP-Menu-Item-Diagnosis-Report"
          onClick={() => openNewReportDialog("Diagnosis")}
        >
          Diagnostic Report
        </MenuItem>
        <MenuItem
          data-qa="CP-Menu-Item-Inspection-Report"
          onClick={() => openNewReportDialog("Inspection")}
        >
          Inspection Report
        </MenuItem>
        <MenuItem
          data-qa="CP-Menu-Item-Repair-Report"
          onClick={() => openNewReportDialog("Repair")}
        >
          Repair Report
        </MenuItem>
        {workOrderAccess ? (
          <MenuItem
            data-qa="CP-Menu-Item-Work-Order"
            onClick={openNewWorkOrderDialog}
          >
            Work Request
          </MenuItem>
        ) : null}
        <MenuItem
          data-qa="CP-Menu-Item-Case-Action"
          onClick={openNewCaseActionDialog}
        >
          Action
        </MenuItem>
      </Menu>
      {newPostDialogOpen && caseData ? (
        <NewPostDialog
          open={newPostDialogOpen}
          setOpen={setNewPostDialogOpen}
          currentCase={caseData}
          refetchStream={() =>
            queryClient.invalidateQueries(["streamFeed", caseId])
          }
        />
      ) : null}
      {newReportDialogOpen ? (
        <NewReportDialog
          open={newReportDialogOpen}
          setOpen={setNewReportDialogOpen}
          reportVariant={newReportVariant}
          currentCase={caseData}
          refetchStream={() =>
            queryClient.invalidateQueries(["streamFeed", caseId])
          }
        />
      ) : null}
      {newWorkOrderDialogOpen ? (
        <NewWorkOrderDialog
          open={newWorkOrderDialogOpen}
          setOpen={setNewWorkOrderDialogOpen}
          caseObject={caseData}
          site={site}
          machine={machine}
          component={component}
          failureMode={failureMode}
          refetchStream={() =>
            queryClient.invalidateQueries(["streamFeed", caseId])
          }
        />
      ) : null}
      {newActionDialogOpen ? (
        <CreateCaseActionDialog
          open={newActionDialogOpen}
          setOpen={setNewActionDialogOpen}
          caseId={caseId}
          addNewAction={() => {
            refetchActions();
            queryClient.invalidateQueries(["streamFeed", caseId]);
          }}
        />
      ) : null}
    </>
  );
}

interface StreamItemProps {
  item: CaseStreamItem;
  caseId: number;
  refetchStream(): Promise<void>;
}

const StreamItem = ({
  item,
  caseId,
  refetchStream
}: StreamItemProps): JSX.Element => {
  switch (item.item_type) {
    case CaseStreamItemType.EVENT:
      return (
        <EventCard
          caseEventId={item.entity_id}
          datetime={item.datetime}
          user={item.user}
          refetchStream={refetchStream}
        />
      );
    case CaseStreamItemType.ASSIGNEE:
      if (item.user) {
        return (
          <TextStreamItem
            text={`${buildUserName(item.user)} was assigned to the case on`}
            date={item.datetime}
          />
        );
      } else {
        return (
          <TextStreamItem text={"Set to unassigned on"} date={item.datetime} />
        );
      }
    case CaseStreamItemType.PROJECT:
      if (item.description !== null) {
        return (
          <TextStreamItem
            text={`Case added to project ${item.description} by ${buildUserName(
              item.user
            )} on`}
            date={item.datetime}
          />
        );
      } else {
        return (
          <TextStreamItem
            text={`Case removed from project by ${buildUserName(item.user)} on`}
            date={item.datetime}
          />
        );
      }
    case CaseStreamItemType.WORK_ORDER:
      return <WorkOrderCard workOrderId={item.entity_id} user={item.user} />;
    case CaseStreamItemType.CASE_ACTION:
      return (
        <CaseActionCard
          caseActionId={item.entity_id}
          caseId={caseId}
          user={item.user}
          datetime={item.datetime}
        />
      );
    default:
      console.error(`Unhandled case stream item type ${item.item_type}`);
      return <></>;
  }
};

const ScrollBar = styled(PerfectScrollbar)`
  padding-right: ${({ theme }) => theme.spacing(2)};
  @media (max-width: 960px) {
    padding-right: 0;
    margin-top: ${({ theme }) => theme.spacing(3)};
  }
`;

const FeedWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AddFeedItemButton = styled(Fab)`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing(0)};
  right: ${({ theme }) => theme.spacing(5)};
  z-index: 20;
  transition: ${({ theme }) =>
    theme.transitions.create(["background", "transform"], {
      duration: theme.transitions.duration.complex
    })};

  &:hover {
    transform: scale(1.1);
  }
`;

const StyledPaper = styled(Paper)`
  padding: ${({ theme }) => theme.spacing(2)};
  margin-bottom: ${({ theme }) => theme.spacing(2)};
`;
StyledPaper.displayName = "StyledPaper";
